import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  AccountsCsvStandardFieldsEnum,
  ContactsManagerCsvErrorsEnum,
  ContactsManagerFileError,
} from '../../../../../../../../../../src/store/panels/contacts/contactsManager/file/contactsManagerFileState';
import {Plural, Trans} from '../../../../../../../../../shared/helpers/src/internationalization/react';
import {AccountsCsvImportFileAttachment} from '../accountsCsvImportFileAttachment';
import {ErrorWarningBox, ErrorWarningBoxTypesEnum} from './errorWarningBox';

/*
 * Constants.
 */

const warningFileName = 'Updated CSV.csv';
const warningFileType = 'text/csv';

/*
 * Props.
 */

export interface AccountsCsvImportErrorWarningBoxProps {
  invalidCount: number;
  fileErrors?: readonly ContactsManagerFileError[];
  fileName?: string;
  fileSize?: number;
  generateWarningFileAsync?: () => Promise<File | undefined>;
}

/*
 * Style.
 */

const StyledFileAttachmentWrapperDiv = styled.div`
  display: flex;
  margin-top: 10px;
  margin-bottom: 3px;
`;

const StyledFileErrorAddtlDescDiv = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${(p) => p.theme.greys.shade80};
  margin-top: 2px;
`;

/*
 * Component.
 */

export const FileErrorWarningBox: FC<AccountsCsvImportErrorWarningBoxProps> = ({
  fileName,
  invalidCount,
  fileSize,
  fileErrors,
  generateWarningFileAsync,
}) => {
  if (fileErrors && fileErrors.length > 0) {
    return (
      <ErrorWarningBox
        type={ErrorWarningBoxTypesEnum.ERROR}
        title={<Trans id="accounts-csv-import-file-error-title">Failed to upload {fileName}</Trans>}
        descriptionList={<>{fileErrors.map(renderErrorListItem)}</>}
        addtlDescription={
          <StyledFileErrorAddtlDescDiv>
            <Trans id="accounts-csv-import-file-error-addtl-description">
              Edit your CSV file and upload again
            </Trans>
          </StyledFileErrorAddtlDescDiv>
        }
      />
    );
  }

  if (!invalidCount) {
    return null;
  }

  return (
    <ErrorWarningBox
      type={ErrorWarningBoxTypesEnum.WARNING}
      title={
        <Plural
          id="acccounts-csv-import-warning-title"
          value={invalidCount}
          one={`${invalidCount} invalid value in your CSV`}
          other={`${invalidCount} invalid values in your CSV`}
        />
      }
      descriptionText={
        <Trans id="accounts-csv-import-warning-content">
          Please download your updated CSV file below to view errors under the prepended “Front upload error”
          column. If you proceed without updating your CSV, invalid values won&apos;t be imported.
        </Trans>
      }
      addtlDescription={
        <StyledFileAttachmentWrapperDiv>
          <AccountsCsvImportFileAttachment
            name={warningFileName}
            size={fileSize || 0}
            contentType={warningFileType}
            generateFileAsync={generateWarningFileAsync}
          />
        </StyledFileAttachmentWrapperDiv>
      }
    />
  );
};

/*
 * Helpers.
 */

function renderErrorListItem(error: ContactsManagerFileError) {
  switch (error.error) {
    case ContactsManagerCsvErrorsEnum.EMPTY_HEADERS: {
      return (
        <li key={error.error}>
          <Trans id="accounts-csv-import-error-empty-headers">
            No header row was found. A header row is required.
          </Trans>
        </li>
      );
    }
    case ContactsManagerCsvErrorsEnum.INVALID_DUPLICATE_HEADERS: {
      const columnsString = buildDuplicateColumnsString(error);
      return (
        <li key={error.error}>
          <Trans id="accounts-csv-import-error-invalid-duplicate-headers">
            Duplicate columns were found{columnsString}. Make sure all columns are unique.
          </Trans>
        </li>
      );
    }
    case ContactsManagerCsvErrorsEnum.TOO_MANY_ROWS: {
      return (
        <li key={error.error}>
          <Trans id="accounts-csv-import-error-too-many-rows">
            File exceeds maximum upload size of 3,000 rows.
          </Trans>
        </li>
      );
    }
    case ContactsManagerCsvErrorsEnum.TOO_MANY_COLUMNS: {
      return (
        <li key={error.error}>
          <Trans id="accounts-csv-import-error-too-many-columns">
            File exceeds maximum upload size of 55 columns.
          </Trans>
        </li>
      );
    }
    case ContactsManagerCsvErrorsEnum.MISSING_HEADER_COLUMN: {
      return (
        <li key={error.error}>
          <Trans id="accounts-csv-import-error-missing-header-column">
            A &quot;name&quot; column was not found. A &quot;name&quot; column is required.
          </Trans>
        </li>
      );
    }
    // We do not expect to hit the default case, but we still show the error in a friendly way as a fallback.
    default: {
      return <li key={error.error}>{_.capitalize(error.error.replaceAll('_', ' '))}.</li>;
    }
  }
}

function buildDuplicateColumnsString(error: ContactsManagerFileError) {
  if (!error.columns) {
    return '';
  }

  const columnsString = _.chain(error.columns)
    .map((col) => {
      const lowerCaseCol = col.toLowerCase();
      // handle externalId as special case since it is a standard header to match info box and template
      return lowerCaseCol === AccountsCsvStandardFieldsEnum.EXTERNALID ? 'externalId' : lowerCaseCol;
    })
    .uniq()
    .value()
    .join(', ');

  return ` (${columnsString})`;
}
